import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import useModuleAccess from "../hooks/useModuleAccess";

const API_URL = process.env.REACT_APP_API_URL;
const S3_BASE_URL =
  "https://instatenders-supplier-upload.s3.ap-south-1.amazonaws.com";

const STOCK_CATEGORY_FILTER_OPTIONS = ["Stock", "Non-Stock"];

const INVENTORY_STAT_CARDS = [
  {
    key: "totalCount",
    label: "Total Count",
    titleColor: "#1F4E79",
    valueColor: "#2874A6",
    filterValue: null,
  },
  {
    key: "stockTotalQuantity",
    label: "Stock Total Qty",
    titleColor: "#196F3D",
    valueColor: "#27AE60",
    filterValue: "Stock",
  },
  {
    key: "nonStockTotalQuantity",
    label: "Non-Stock Total Qty",
    titleColor: "#9A7D0A",
    valueColor: "#F39C12",
    filterValue: "Non-Stock",
  },
];

const DEFAULT_INVENTORY_STATS = INVENTORY_STAT_CARDS.reduce(
  (acc, card) => ({ ...acc, [card.key]: 0 }),
  {},
);

function normalizeInventoryStats(data) {
  if (!data || typeof data !== "object") return { ...DEFAULT_INVENTORY_STATS };
  return INVENTORY_STAT_CARDS.reduce(
    (acc, card) => ({
      ...acc,
      [card.key]: Number(data[card.key]) || 0,
    }),
    {},
  );
}

const InventoryStatCardSkeleton = () => (
  <div className="inventory-stat-col">
    <div className="card supplier-stat-card">
      <div className="card-body">
        <div className="stat-skeleton stat-skeleton-title" aria-hidden="true" />
        <div className="stat-skeleton stat-skeleton-value" aria-hidden="true" />
      </div>
    </div>
  </div>
);

const InventoryStatCard = ({
  label,
  value,
  titleColor,
  valueColor,
  onClick,
  isActive,
}) => (
  <div className="inventory-stat-col">
    <div
      className={`card supplier-stat-card procurement-stat-card-clickable${
        isActive ? " procurement-stat-card-active" : ""
      }`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      role="button"
      tabIndex={0}
      title={onClick ? `Filter by ${label}` : undefined}
    >
      <div className="card-body">
        <div className="stat-title" style={{ color: titleColor }}>
          {label}
        </div>
        <div className="stat-value" style={{ color: valueColor }}>
          {value}
        </div>
      </div>
    </div>
  </div>
);

const INVENTORY_SORT_FIELDS = [
  "materialCode",
  "productName",
  "hsnCode",
  "uom",
  "stockCategory",
  "stockQuantity",
  "status",
  "createdAt",
  "updatedAt",
];

const DEFAULT_SORT_BY = "createdAt";
const DEFAULT_SORT_ORDER = "desc";
const DEFAULT_VIEW_MODE = "grid";

function normalizeSortBy(value) {
  const field = String(value || "").trim();
  return INVENTORY_SORT_FIELDS.includes(field) ? field : DEFAULT_SORT_BY;
}

function normalizeSortOrder(value) {
  const order = String(value || "").trim().toLowerCase();
  return order === "asc" ? "asc" : "desc";
}

function normalizeViewMode(value) {
  return String(value || "").trim().toLowerCase() === "list" ? "list" : "grid";
}

function parseApiResponse(raw) {
  let data = raw;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return raw;
    }
  }
  if (data?.body != null) {
    if (typeof data.body === "string") {
      try {
        return JSON.parse(data.body);
      } catch {
        return data.body;
      }
    }
    return data.body;
  }
  return data;
}

function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeApiPayload(raw) {
  let obj = raw;
  if (!obj || typeof obj !== "object") return raw;
  obj = { ...obj };
  if (typeof obj.data === "string") {
    obj.data = parseJsonIfString(obj.data);
  }
  if (
    obj.data &&
    typeof obj.data === "object" &&
    typeof obj.data.data === "string"
  ) {
    obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) };
  }
  return obj;
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== "object") return false;
  const success = responseData.success;
  return (
    success === true ||
    success === "true" ||
    success === 1 ||
    success === "1"
  );
}

function extractInventoryPayload(responseData) {
  const root = responseData;
  let dataBlock = root?.data != null ? root.data : root;

  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }
  if (!dataBlock || typeof dataBlock !== "object") {
    return { inventories: [], pagination: {}, sortBy: null, sortOrder: null };
  }

  if (Array.isArray(dataBlock)) {
    return {
      inventories: dataBlock,
      pagination:
        root.pagination && typeof root.pagination === "object"
          ? root.pagination
          : {},
      sortBy: root.sortBy || null,
      sortOrder: root.sortOrder || null,
    };
  }

  let list =
    dataBlock.inventories ||
    dataBlock.inventoryList ||
    dataBlock.items ||
    root.inventories;

  if (list == null && dataBlock.data != null) {
    const inner =
      typeof dataBlock.data === "string"
        ? parseJsonIfString(dataBlock.data)
        : dataBlock.data;
    if (inner && typeof inner === "object") {
      list = inner.inventories || inner.inventoryList || inner.items;
    }
  }

  if (typeof list === "string") {
    list = parseJsonIfString(list);
  }

  const pagination =
    (dataBlock && dataBlock.pagination) || root.pagination || {};

  const total =
    typeof pagination.total === "number"
      ? pagination.total
      : typeof pagination.totalCount === "number"
        ? pagination.totalCount
        : typeof dataBlock.totalCount === "number"
          ? dataBlock.totalCount
          : typeof dataBlock.total === "number"
            ? dataBlock.total
            : undefined;

  return {
    inventories: Array.isArray(list) ? list : [],
    pagination:
      pagination && typeof pagination === "object" && !Array.isArray(pagination)
        ? { ...pagination, ...(total != null ? { total } : {}) }
        : total != null
          ? { total }
          : {},
    sortBy: dataBlock.sortBy || root.sortBy || null,
    sortOrder: dataBlock.sortOrder || root.sortOrder || null,
  };
}

function buildS3Url(s3Key) {
  const key = String(s3Key || "").trim().replace(/^\/+/, "");
  if (!key) return "";
  if (/^https?:\/\//i.test(key)) return key;
  return `${S3_BASE_URL}/${key}`;
}

function getInventoryImageUrl(item) {
  if (!item) return "";
  if (typeof item === "string") return buildS3Url(item);
  return (
    item.url ||
    item.imageUrl ||
    item.publicUrl ||
    buildS3Url(item.s3Key || item.key)
  );
}

function normalizeInventoryMediaList(rawList, fallbackLabel) {
  if (!Array.isArray(rawList) || rawList.length === 0) return [];

  return rawList
    .map((item, index) => {
      if (typeof item === "string") {
        const url = buildS3Url(item);
        return url
          ? {
              url,
              s3Key: item,
              originalFileName: `${fallbackLabel} ${index + 1}`,
              contentType: "",
            }
          : null;
      }
      if (!item || typeof item !== "object") return null;
      const url = getInventoryImageUrl(item);
      if (!url) return null;
      return {
        url,
        s3Key: item.s3Key || item.key || "",
        originalFileName:
          item.originalFileName ||
          item.fileName ||
          item.name ||
          `${fallbackLabel} ${index + 1}`,
        contentType: item.contentType || "",
        uploadedAt: item.uploadedAt || "",
      };
    })
    .filter(Boolean);
}

function getInventoryImages(item) {
  if (!item || typeof item !== "object") return [];
  const fromRoot = normalizeInventoryMediaList(item.images, "Image");
  if (fromRoot.length) return fromRoot;
  const material = item.material || item.materialRef || null;
  return normalizeInventoryMediaList(material?.images, "Image");
}

function getInventoryAttachments(item) {
  if (!item || typeof item !== "object") return [];
  const fromRoot = normalizeInventoryMediaList(item.attachments, "Attachment");
  if (fromRoot.length) return fromRoot;
  const material = item.material || item.materialRef || null;
  return normalizeInventoryMediaList(material?.attachments, "Attachment");
}

function isImageMedia(media) {
  const type = String(media?.contentType || "").toLowerCase();
  if (type.startsWith("image/")) return true;
  const name = String(media?.originalFileName || "").toLowerCase();
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
}

function attachmentIconClass(media) {
  const type = String(media?.contentType || "").toLowerCase();
  const name = String(media?.originalFileName || "").toLowerCase();
  if (type.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name)) {
    return "fa-solid fa-image";
  }
  if (type.includes("pdf") || name.endsWith(".pdf")) {
    return "fa-solid fa-file-pdf";
  }
  if (
    type.includes("sheet") ||
    type.includes("excel") ||
    /\.(xlsx?|csv)$/i.test(name)
  ) {
    return "fa-solid fa-file-excel";
  }
  if (type.includes("word") || /\.(docx?)$/i.test(name)) {
    return "fa-solid fa-file-word";
  }
  if (type.startsWith("text/") || name.endsWith(".txt")) {
    return "fa-solid fa-file-lines";
  }
  return "fa-solid fa-paperclip";
}

const InventoryViewImages = ({ images = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageErrors, setImageErrors] = useState({});

  useEffect(() => {
    setCurrentIndex(0);
    setImageErrors({});
  }, [images]);

  if (!images.length) {
    return <div className="text-muted small">No images</div>;
  }

  const current = images[currentIndex];
  const hasMultiple = images.length > 1;
  const showImage = current?.url && !imageErrors[currentIndex];

  return (
    <div>
      <div
        style={{
          width: "100%",
          maxWidth: "280px",
          minHeight: "160px",
          borderRadius: "8px",
          overflow: "hidden",
          border: "1px solid #dee2e6",
          backgroundColor: "#f8f9fa",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        {showImage ? (
          <img
            src={current.url}
            alt={current.originalFileName || "Inventory image"}
            onError={() =>
              setImageErrors((prev) => ({ ...prev, [currentIndex]: true }))
            }
            style={{
              maxWidth: "100%",
              maxHeight: "220px",
              objectFit: "contain",
            }}
          />
        ) : (
          <div className="text-muted py-4 small">Image unavailable</div>
        )}

        {hasMultiple && showImage && (
          <>
            <button
              type="button"
              className="btn btn-sm btn-dark position-absolute"
              style={{
                left: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                opacity: 0.8,
                borderRadius: "50%",
                width: "28px",
                height: "28px",
                padding: 0,
              }}
              onClick={() =>
                setCurrentIndex((prev) =>
                  prev === 0 ? images.length - 1 : prev - 1,
                )
              }
              title="Previous image"
            >
              <i className="fa-solid fa-chevron-left" />
            </button>
            <button
              type="button"
              className="btn btn-sm btn-dark position-absolute"
              style={{
                right: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                opacity: 0.8,
                borderRadius: "50%",
                width: "28px",
                height: "28px",
                padding: 0,
              }}
              onClick={() =>
                setCurrentIndex((prev) =>
                  prev === images.length - 1 ? 0 : prev + 1,
                )
              }
              title="Next image"
            >
              <i className="fa-solid fa-chevron-right" />
            </button>
            <div
              className="position-absolute bg-dark text-white px-2 py-1"
              style={{
                bottom: "8px",
                right: "8px",
                fontSize: "11px",
                borderRadius: "12px",
                opacity: 0.85,
              }}
            >
              {currentIndex + 1} / {images.length}
            </div>
          </>
        )}
      </div>

      {images.length > 1 && (
        <div className="d-flex flex-wrap mt-2" style={{ gap: "8px" }}>
          {images.map((image, index) => (
            <button
              key={`${image.url}-${index}`}
              type="button"
              onClick={() => setCurrentIndex(index)}
              style={{
                width: "52px",
                height: "52px",
                borderRadius: "6px",
                overflow: "hidden",
                border:
                  index === currentIndex
                    ? "2px solid #1199B2"
                    : "1px solid #dee2e6",
                padding: 0,
                background: "#fff",
              }}
              title={image.originalFileName}
            >
              {!imageErrors[index] ? (
                <img
                  src={image.url}
                  alt={image.originalFileName || `Thumbnail ${index + 1}`}
                  onError={() =>
                    setImageErrors((prev) => ({ ...prev, [index]: true }))
                  }
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <span className="text-muted" style={{ fontSize: "10px" }}>
                  N/A
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const InventoryViewAttachments = ({ attachments = [] }) => {
  if (!attachments.length) {
    return <div className="text-muted small">No attachments</div>;
  }

  return (
    <ul className="list-group">
      {attachments.map((att, index) => (
        <li
          key={`${att.s3Key || att.url}-${index}`}
          className="list-group-item d-flex justify-content-between align-items-center py-2 px-3"
        >
          <div className="d-flex align-items-center text-truncate mr-2">
            <i className={`${attachmentIconClass(att)} mr-2`} />
            <span className="text-truncate" title={att.originalFileName}>
              {att.originalFileName}
            </span>
          </div>
          <a
            href={att.url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-outline-secondary"
            title={isImageMedia(att) ? "Open" : "Download / Open"}
          >
            <i
              className={
                isImageMedia(att) ? "fa-solid fa-eye" : "fa-solid fa-download"
              }
            />
          </a>
        </li>
      ))}
    </ul>
  );
};

function displayValue(value) {
  if (value == null || value === "") return "-";
  return String(value);
}

function displayUser(ref) {
  if (!ref) return "-";
  if (typeof ref === "string") return ref.trim() || "-";
  return ref.name || ref.userName || ref.email || "-";
}

function resolveHistoryUpdatedBy(updatedBy, inventoryItem) {
  if (updatedBy && typeof updatedBy === "object") {
    return displayUser(updatedBy);
  }

  const updatedById = String(updatedBy || "").trim();
  if (!updatedById) return "-";

  const candidates = [
    inventoryItem?.updatedBy,
    inventoryItem?.createdBy,
    inventoryItem?.userId,
  ];

  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object") continue;
    const candidateId = String(candidate._id || candidate.id || "").trim();
    if (candidateId && candidateId === updatedById) {
      return displayUser(candidate);
    }
  }

  return updatedById;
}

function formatDateToDisplay(dateString) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return displayValue(dateString);
  return date.toLocaleDateString("en-GB", {
    timeZone: "Asia/Kolkata",
  });
}

function renderStatusBadge(status) {
  const value = String(status || "").trim();
  if (!value) return "-";
  const normalized = value.toLowerCase();
  const badgeClass =
    normalized === "active"
      ? "badge-success"
      : normalized === "inactive"
        ? "badge-danger"
        : "badge-secondary";
  return (
    <span
      className={`badge ${badgeClass}`}
      style={{ fontSize: "90%", fontWeight: 550 }}
    >
      {value}
    </span>
  );
}

function getInventoryHistory(item) {
  const history = Array.isArray(item?.History)
    ? item.History
    : Array.isArray(item?.history)
      ? item.history
      : [];
  return history;
}

function getInventoryRecordId(item) {
  return item?._id || item?.id || item?.inventoryId || "";
}

function pickInventoryFromResponse(responseData, id) {
  let dataBlock = responseData?.data != null ? responseData.data : responseData;
  if (dataBlock == null) return null;
  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }
  if (!dataBlock || typeof dataBlock !== "object") return null;

  if (dataBlock.inventory && typeof dataBlock.inventory === "object") {
    return dataBlock.inventory;
  }

  if (
    !Array.isArray(dataBlock) &&
    dataBlock.inventories == null &&
    (dataBlock._id != null ||
      dataBlock.id != null ||
      dataBlock.materialCode != null ||
      dataBlock.productName != null)
  ) {
    return dataBlock;
  }

  const list = dataBlock.inventories;
  if (!Array.isArray(list) || list.length === 0) return null;
  const found = list.find((row) => String(row._id || row.id) === String(id));
  return found || list[0];
}

const InventoryDetailCard = ({ label, value, icon }) => (
  <div
    className="h-100"
    style={{
      backgroundColor: "#f8fafb",
      border: "1px solid #e3e8ee",
      borderRadius: "8px",
      padding: "14px 16px",
    }}
  >
    <div
      className="d-flex align-items-center mb-2"
      style={{ color: "#6c757d", fontSize: "12px", fontWeight: 600 }}
    >
      {icon ? (
        <i className={`${icon} mr-2`} style={{ color: "#1199B2" }} />
      ) : null}
      {label}
    </div>
    <div
      style={{
        color: "#212529",
        fontSize: "14px",
        fontWeight: 600,
        wordBreak: "break-word",
      }}
    >
      {value || "-"}
    </div>
  </div>
);

const STOCK_OPERATIONS = [
  { value: "add", label: "Add" },
  { value: "subtract", label: "Subtract" },
];

const Inventory = () => {
  const { canView, canUpdate } = useModuleAccess("inventory_management");
  const savedFilters = JSON.parse(
    sessionStorage.getItem("inventoryFilters") || "{}",
  );

  const [inventories, setInventories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(
    savedFilters.searchQuery || "",
  );
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    savedFilters.activeSearchQuery || "",
  );
  const [filterStockCategory, setFilterStockCategory] = useState(
    savedFilters.filterStockCategory || "",
  );
  const [activeFilterStockCategory, setActiveFilterStockCategory] = useState(
    savedFilters.activeFilterStockCategory || "",
  );
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(
    savedFilters.currentPage || 1,
  );
  const [itemsPerPage, setItemsPerPage] = useState(
    savedFilters.itemsPerPage || 25,
  );
  const [sortBy, setSortBy] = useState(
    normalizeSortBy(savedFilters.sortBy || DEFAULT_SORT_BY),
  );
  const [sortOrder, setSortOrder] = useState(
    normalizeSortOrder(savedFilters.sortOrder || DEFAULT_SORT_ORDER),
  );
  const [viewMode, setViewMode] = useState(
    normalizeViewMode(savedFilters.viewMode || DEFAULT_VIEW_MODE),
  );

  const [viewItem, setViewItem] = useState(null);
  const [isLoadingViewItem, setIsLoadingViewItem] = useState(false);
  const [historyItem, setHistoryItem] = useState(null);
  const [isLoadingHistoryItem, setIsLoadingHistoryItem] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editCount, setEditCount] = useState("");
  const [editOperation, setEditOperation] = useState("add");
  const [isUpdatingStock, setIsUpdatingStock] = useState(false);
  const [inventoryStats, setInventoryStats] = useState(DEFAULT_INVENTORY_STATS);
  const [isInventoryCountLoading, setIsInventoryCountLoading] = useState(true);

  const fetchInventoryCounts = useCallback(async () => {
    setIsInventoryCountLoading(true);
    try {
      const response = await axios.post(API_URL, {
        action: "inventory-counts",
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      if (isApiSuccess(responseData)) {
        setInventoryStats(normalizeInventoryStats(responseData.data));
      } else {
        setInventoryStats({ ...DEFAULT_INVENTORY_STATS });
      }
    } catch (error) {
      console.error("Fetch inventory counts error:", error);
      setInventoryStats({ ...DEFAULT_INVENTORY_STATS });
    } finally {
      setIsInventoryCountLoading(false);
    }
  }, []);

  const fetchInventories = useCallback(async () => {
    setIsLoading(true);
    try {
      const payload = {
        action: "get-all-inventory",
        page: currentPage,
        limit: itemsPerPage,
        sortBy: normalizeSortBy(sortBy),
        sortOrder: normalizeSortOrder(sortOrder),
      };

      const trimmedSearch = activeSearchQuery
        ? String(activeSearchQuery).trim()
        : "";
      if (trimmedSearch) {
        payload.search = trimmedSearch;
      }

      if (activeFilterStockCategory) {
        payload.stockCategory = activeFilterStockCategory;
      }

      const response = await axios.post(API_URL, payload);
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      const {
        inventories: list,
        pagination,
        sortBy: responseSortBy,
        sortOrder: responseSortOrder,
      } = extractInventoryPayload(responseData);

      const ok =
        isApiSuccess(responseData) || (Array.isArray(list) && list.length > 0);

      if (ok) {
        setInventories(list);
        const total =
          typeof pagination.total === "number"
            ? pagination.total
            : typeof pagination.totalCount === "number"
              ? pagination.totalCount
              : list.length;
        setTotalCount(total);

        if (responseSortBy && INVENTORY_SORT_FIELDS.includes(responseSortBy)) {
          setSortBy(responseSortBy);
        }
        if (responseSortOrder === "asc" || responseSortOrder === "desc") {
          setSortOrder(responseSortOrder);
        }
      } else {
        toast.error(
          responseData?.message || "Failed to fetch inventory",
          { autoClose: 2000 },
        );
        setInventories([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Fetch inventory error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        (typeof errData === "object" && errData?.message) ||
          "An error occurred while fetching inventory",
        { autoClose: 2000 },
      );
      setInventories([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    activeSearchQuery,
    activeFilterStockCategory,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    fetchInventories();
  }, [fetchInventories]);

  useEffect(() => {
    fetchInventoryCounts();
  }, [fetchInventoryCounts]);

  useEffect(() => {
    sessionStorage.setItem(
      "inventoryFilters",
      JSON.stringify({
        searchQuery,
        activeSearchQuery,
        filterStockCategory,
        activeFilterStockCategory,
        currentPage,
        itemsPerPage,
        sortBy,
        sortOrder,
        viewMode,
      }),
    );
  }, [
    searchQuery,
    activeSearchQuery,
    filterStockCategory,
    activeFilterStockCategory,
    currentPage,
    itemsPerPage,
    sortBy,
    sortOrder,
    viewMode,
  ]);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1);
  const startItem =
    inventories.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount);
  const hasActiveFilters = Boolean(
    activeSearchQuery || activeFilterStockCategory,
  );

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value) || 25);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    const next = Math.min(Math.max(1, page), totalPages);
    setCurrentPage(next);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i += 1) pages.push(i);
      return pages;
    }

    pages.push(1);
    let start = Math.max(2, currentPage - 1);
    let end = Math.min(totalPages - 1, currentPage + 1);

    if (currentPage <= 3) {
      start = 2;
      end = 4;
    } else if (currentPage >= totalPages - 2) {
      start = totalPages - 3;
      end = totalPages - 1;
    }

    if (start > 2) pages.push("ellipsis");
    for (let i = start; i <= end; i += 1) pages.push(i);
    if (end < totalPages - 1) pages.push("ellipsis");
    pages.push(totalPages);
    return pages;
  };

  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);

    const trimmedValue = newValue ? newValue.trim() : "";
    if (trimmedValue === "" && activeSearchQuery !== "") {
      setActiveSearchQuery("");
      if (currentPage !== 1) setCurrentPage(1);
    }
  };

  const handleSearchTrigger = () => {
    const trimmedQuery = searchQuery ? searchQuery.trim() : "";
    setActiveSearchQuery(trimmedQuery);
    if (currentPage !== 1) setCurrentPage(1);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchTrigger();
    }
  };

  const handleStockCategoryFilterChange = (e) => {
    const value = e.target.value;
    setFilterStockCategory(value);
    setActiveFilterStockCategory(value);
    if (currentPage !== 1) setCurrentPage(1);
  };

  const isStatCardActive = (card) => {
    if (!card.filterValue) {
      return !activeFilterStockCategory;
    }
    return activeFilterStockCategory === card.filterValue;
  };

  const handleStatCardClick = (card) => {
    if (!card.filterValue) {
      setFilterStockCategory("");
      setActiveFilterStockCategory("");
    } else if (activeFilterStockCategory === card.filterValue) {
      setFilterStockCategory("");
      setActiveFilterStockCategory("");
    } else {
      setFilterStockCategory(card.filterValue);
      setActiveFilterStockCategory(card.filterValue);
    }
    if (currentPage !== 1) setCurrentPage(1);
  };

  const handleSort = (column) => {
    const apiField = normalizeSortBy(column);
    if (!INVENTORY_SORT_FIELDS.includes(apiField)) return;

    if (sortBy === apiField) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(apiField);
      setSortOrder("asc");
    }
    if (currentPage !== 1) setCurrentPage(1);
  };

  const getSortIcon = (column) => {
    const apiField = normalizeSortBy(column);
    if (!apiField || sortBy !== apiField) {
      return (
        <i
          className="fa-solid fa-sort ml-1"
          style={{ opacity: 0.35, fontSize: "11px" }}
        />
      );
    }
    return (
      <i
        className={`fa-solid fa-sort-${sortOrder === "asc" ? "up" : "down"} ml-1`}
        style={{ fontSize: "11px" }}
      />
    );
  };

  const renderSortableHeader = (column, label, style = {}) => (
    <th
      style={{ whiteSpace: "nowrap", cursor: "pointer", ...style }}
      onClick={() => handleSort(column)}
    >
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        {label}
        {getSortIcon(column)}
      </span>
    </th>
  );

  const openBootstrapModal = (modalId) => {
    if (window.$) {
      window.$(modalId).modal("show");
    }
  };

  const closeBootstrapModal = (modalId) => {
    if (window.$) {
      window.$(modalId).modal("hide");
    }
  };

  const fetchInventoryById = async (item) => {
    const inventoryId = getInventoryRecordId(item);
    const materialCode = String(item?.materialCode || "").trim();

    if (!inventoryId && !materialCode) {
      toast.error("Inventory id or material code is missing", {
        autoClose: 2000,
      });
      return null;
    }

    const payload = {
      action: "get-inventory-by-id",
    };
    if (inventoryId) payload.id = inventoryId;
    if (materialCode) payload.materialCode = materialCode;

    const response = await axios.post(API_URL, payload);
    const responseData = normalizeApiPayload(parseApiResponse(response.data));

    if (!isApiSuccess(responseData)) {
      const message =
        responseData?.message || "Failed to fetch inventory details";
      throw Object.assign(new Error(message), { responseData });
    }

    const row = pickInventoryFromResponse(responseData, inventoryId);
    if (!row) {
      throw new Error("Inventory not found");
    }
    return row;
  };

  const handleViewClick = async (item) => {
    setViewItem(null);
    setIsLoadingViewItem(true);
    openBootstrapModal("#modalviewinventory");

    try {
      const row = await fetchInventoryById(item);
      if (row) {
        setViewItem(row);
      } else {
        closeBootstrapModal("#modalviewinventory");
      }
    } catch (error) {
      console.error("Fetch inventory by id error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        (typeof errData === "object" && errData?.message) ||
          error.message ||
          "An error occurred while fetching inventory details",
        { autoClose: 2000 },
      );
      closeBootstrapModal("#modalviewinventory");
    } finally {
      setIsLoadingViewItem(false);
    }
  };

  const handleHistoryClick = async (item) => {
    setHistoryItem(null);
    setIsLoadingHistoryItem(true);
    openBootstrapModal("#modalinventoryhistory");

    try {
      const row = await fetchInventoryById(item);
      if (row) {
        setHistoryItem(row);
      } else {
        closeBootstrapModal("#modalinventoryhistory");
      }
    } catch (error) {
      console.error("Fetch inventory history error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        (typeof errData === "object" && errData?.message) ||
          error.message ||
          "An error occurred while fetching inventory history",
        { autoClose: 2000 },
      );
      closeBootstrapModal("#modalinventoryhistory");
    } finally {
      setIsLoadingHistoryItem(false);
    }
  };

  const handleEditClick = (item) => {
    setEditItem(item);
    setEditCount("");
    setEditOperation("add");
    openBootstrapModal("#modaleditinventory");
  };

  const handleCloseViewModal = () => {
    closeBootstrapModal("#modalviewinventory");
    setViewItem(null);
    setIsLoadingViewItem(false);
  };

  const handleCloseHistoryModal = () => {
    closeBootstrapModal("#modalinventoryhistory");
    setHistoryItem(null);
    setIsLoadingHistoryItem(false);
  };

  const handleCloseEditModal = () => {
    if (isUpdatingStock) return;
    closeBootstrapModal("#modaleditinventory");
    setEditItem(null);
    setEditCount("");
    setEditOperation("add");
  };

  const handleUpdateStock = async () => {
    if (!editItem) return;

    const inventoryId = getInventoryRecordId(editItem);
    const materialCode = String(editItem.materialCode || "").trim();
    const countValue = Number(editCount);

    if (!inventoryId && !materialCode) {
      toast.error("Inventory id or material code is missing", {
        autoClose: 2000,
      });
      return;
    }

    if (editCount === "" || Number.isNaN(countValue) || countValue < 0) {
      toast.error("Valid count / stockQuantity is required", {
        autoClose: 2000,
      });
      return;
    }

    if (!["add", "subtract", "set"].includes(editOperation)) {
      toast.error("operation must be one of 'add', 'subtract' or 'set'", {
        autoClose: 2000,
      });
      return;
    }

    const updatedBy = localStorage.getItem("id");
    if (!updatedBy) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      return;
    }

    setIsUpdatingStock(true);
    try {
      const payload = {
        action: "update-inventory-stock",
        count: countValue,
        operation: editOperation,
        updatedBy,
      };

      if (inventoryId) payload.id = inventoryId;
      if (materialCode) payload.materialCode = materialCode;

      const response = await axios.post(API_URL, payload);
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (isApiSuccess(responseData)) {
        toast.success(
          responseData?.message || "Inventory stock updated successfully",
          { autoClose: 2000 },
        );
        closeBootstrapModal("#modaleditinventory");
        setEditItem(null);
        setEditCount("");
        setEditOperation("add");
        fetchInventories();
        fetchInventoryCounts();
      } else {
        toast.error(
          responseData?.message || "Failed to update inventory stock",
          { autoClose: 2500 },
        );
      }
    } catch (error) {
      console.error("Update inventory stock error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        (typeof errData === "object" && errData?.message) ||
          "An error occurred while updating inventory stock",
        { autoClose: 2500 },
      );
    } finally {
      setIsUpdatingStock(false);
    }
  };

  const renderActionButtons = (item) => {
    const id = getInventoryRecordId(item);
    if (!id && !item?.materialCode) return null;

    return (
      <div
        align="center"
        style={{ whiteSpace: "nowrap" }}
        className="d-flex justify-content-center align-items-center"
      >
        {canView && (
          <a
            href="#modalviewinventory"
            className="crsr_pntr action-icon-link mr-2"
            title="View"
            onClick={(e) => {
              e.preventDefault();
              handleViewClick(item);
            }}
          >
            <i className="fa-solid fa-eye" />
          </a>
        )}
        {canUpdate && (
          <a
            href="#modaleditinventory"
            className="crsr_pntr action-icon-link mr-2"
            title="Update Stock"
            onClick={(e) => {
              e.preventDefault();
              handleEditClick(item);
            }}
          >
            <i className="fa-solid fa-pen" />
          </a>
        )}
        <a
          href="#modalinventoryhistory"
          className="crsr_pntr action-icon-link"
          title="History"
          onClick={(e) => {
            e.preventDefault();
            handleHistoryClick(item);
          }}
        >
          <i className="fa-solid fa-clock-rotate-left" />
        </a>
      </div>
    );
  };

  const renderPagination = () => (
    <div className="supply-pagination-container">
      <div className="pagination-wrapper">
        <div className="pagination-info">
          <label className="mb-0 mr-2" style={{ fontSize: "13px" }}>
            Show:
          </label>
          <select
            className="form-control form-control-sm"
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
          >
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
          <span className="ml-2" style={{ fontSize: "13px" }}>
            entries
          </span>
          <span className="ml-3 text-muted" style={{ fontSize: "13px" }}>
            {startItem}–{endItem} of {totalCount}
          </span>
        </div>

        <nav className="pagination-nav">
          <ul className="pagination mb-0">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                title="First Page"
              >
                <i className="fa-solid fa-angle-double-left" />
              </button>
            </li>
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                title="Previous Page"
              >
                <i className="fa-solid fa-angle-left" />
              </button>
            </li>

            {getPageNumbers().map((page, index) =>
              page === "ellipsis" ? (
                <li
                  key={`ellipsis-${index}`}
                  className="page-item disabled"
                >
                  <span className="page-link">...</span>
                </li>
              ) : (
                <li
                  key={page}
                  className={`page-item ${currentPage === page ? "active" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                </li>
              ),
            )}

            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                title="Next Page"
              >
                <i className="fa-solid fa-angle-right" />
              </button>
            </li>
            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                title="Last Page"
              >
                <i className="fa-solid fa-angle-double-right" />
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );

  const renderGridView = () => (
    <div>
      <div className="row">
        {inventories.map((item, index) => {
          const id = getInventoryRecordId(item) || index;
          const images = Array.isArray(item.images) ? item.images : [];
          const firstImageUrl = getInventoryImageUrl(images[0]);

          return (
            <div
              key={id}
              className="col-xl-3 col-lg-4 col-md-6 col-sm-6 mb-3"
            >
              <div
                className="card h-100"
                style={{
                  borderRadius: "10px",
                  border: "1px solid #e3e8ee",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                }}
              >
                <div
                  style={{
                    height: "160px",
                    backgroundColor: "#f4f7f9",
                    borderTopLeftRadius: "10px",
                    borderTopRightRadius: "10px",
                    overflow: "hidden",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "8px",
                  }}
                >
                  {firstImageUrl ? (
                    <img
                      src={firstImageUrl}
                      alt={item.productName || "inventory"}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <i
                      className="fa-solid fa-box"
                      style={{ fontSize: "42px", color: "#b0bec5" }}
                    />
                  )}
                </div>
                <div className="card-body" style={{ padding: "14px 16px" }}>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div
                      style={{
                        color: "#1199B2",
                        fontSize: "12px",
                        fontWeight: 700,
                      }}
                    >
                      {displayValue(item.materialCode)}
                    </div>
                    {renderStatusBadge(item.status)}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#212529",
                      marginBottom: "8px",
                      minHeight: "40px",
                      wordBreak: "break-word",
                    }}
                  >
                    {displayValue(item.productName)}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6c757d",
                      marginBottom: "4px",
                    }}
                  >
                    <strong>Stock Category:</strong>{" "}
                    {displayValue(item.stockCategory)}
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#212529",
                      fontWeight: 700,
                      marginBottom: "6px",
                    }}
                  >
                    Stock Qty:{" "}
                    {item.stockQuantity != null ? item.stockQuantity : "-"}
                  </div>
                  <div
                    className="d-flex justify-content-between"
                    style={{
                      fontSize: "12px",
                      color: "#6c757d",
                      marginBottom: "6px",
                    }}
                  >
                    <span>
                      <strong>HSN:</strong> {displayValue(item.hsnCode)}
                    </span>
                    <span>
                      <strong>UOM:</strong> {displayValue(item.uom)}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#6c757d",
                      marginBottom: "10px",
                    }}
                  >
                    Updated:{" "}
                    {formatDateToDisplay(item.updatedAt || item.createdAt)}
                  </div>
                  <div className="d-flex justify-content-end">
                    {renderActionButtons(item)}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {renderPagination()}
    </div>
  );

  const renderListView = () => (
    <div className="supply-table-container">
      <div className="table-responsive">
        <table
          id="inventories"
          width="100%"
          className="table table-striped table-bordered txt_lft"
        >
          <thead>
            <tr>
              <th
                style={{
                  whiteSpace: "nowrap",
                  width: "40px",
                  minWidth: "40px",
                }}
              >
                S.No
              </th>
              <th style={{ minWidth: "70px" }}>Image</th>
              {renderSortableHeader("materialCode", "Material Code", {
                minWidth: "130px",
              })}
              {renderSortableHeader("productName", "Product Name", {
                minWidth: "180px",
              })}
              {renderSortableHeader("hsnCode", "HSN", { minWidth: "90px" })}
              {renderSortableHeader("uom", "UOM", { minWidth: "70px" })}
              {renderSortableHeader("stockCategory", "Stock Category", {
                minWidth: "120px",
              })}
              {renderSortableHeader("stockQuantity", "Stock Qty", {
                minWidth: "100px",
              })}
              {renderSortableHeader("status", "Status", { minWidth: "90px" })}
              <th style={{ minWidth: "120px" }}>Updated By</th>
              {renderSortableHeader("updatedAt", "Updated At", {
                minWidth: "110px",
              })}
              <th
                style={{
                  width: "100px",
                  minWidth: "100px",
                  textAlign: "center",
                }}
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {inventories.map((item, index) => {
              const id = getInventoryRecordId(item) || index;
              const images = Array.isArray(item.images) ? item.images : [];
              const firstImageUrl = getInventoryImageUrl(images[0]);

              return (
                <tr key={id}>
                  <td>
                    {(currentPage - 1) * itemsPerPage + index + 1}
                  </td>
                  <td>
                    <div
                      style={{
                        width: "50px",
                        height: "50px",
                        borderRadius: "6px",
                        overflow: "hidden",
                        backgroundColor: "#f4f7f9",
                        border: "1px solid #e3e8ee",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {firstImageUrl ? (
                        <img
                          src={firstImageUrl}
                          alt={item.productName || "inventory"}
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <i
                          className="fa-solid fa-box"
                          style={{
                            fontSize: "18px",
                            color: "#b0bec5",
                          }}
                        />
                      )}
                    </div>
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {displayValue(item.materialCode)}
                  </td>
                  <td
                    style={{
                      maxWidth: "220px",
                      wordWrap: "break-word",
                      whiteSpace: "normal",
                    }}
                  >
                    {displayValue(item.productName)}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {displayValue(item.hsnCode)}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {displayValue(item.uom)}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {displayValue(item.stockCategory)}
                  </td>
                  <td
                    style={{
                      whiteSpace: "nowrap",
                      fontWeight: 600,
                    }}
                  >
                    {item.stockQuantity != null ? item.stockQuantity : "-"}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {renderStatusBadge(item.status)}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {displayUser(item.updatedBy || item.createdBy)}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {formatDateToDisplay(item.updatedAt || item.createdAt)}
                  </td>
                  <td>{renderActionButtons(item)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {renderPagination()}
    </div>
  );

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">Inventory</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="inventory-stat-card-row">
                {isInventoryCountLoading
                  ? INVENTORY_STAT_CARDS.map((card) => (
                      <InventoryStatCardSkeleton key={card.key} />
                    ))
                  : INVENTORY_STAT_CARDS.map((card) => (
                      <InventoryStatCard
                        key={card.key}
                        label={card.label}
                        value={inventoryStats[card.key]}
                        titleColor={card.titleColor}
                        valueColor={card.valueColor}
                        isActive={isStatCardActive(card)}
                        onClick={() => handleStatCardClick(card)}
                      />
                    ))}
              </div>

              <div>
                <div className="card-body">
                  <div className="row mb-3 align-items-end">
                    <div className="col-md-4">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Search
                        </label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search material code, name, HSN, UOM..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyDown={handleSearchKeyDown}
                          />
                          <div className="input-group-append">
                            <div
                              className="input-group-text crsr_pntr"
                              onClick={handleSearchTrigger}
                              style={{ cursor: "pointer" }}
                              title="Search"
                            >
                              <i className="fa-solid fa-search" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Stock Category
                        </label>
                        <select
                          className="form-control"
                          value={filterStockCategory}
                          onChange={handleStockCategoryFilterChange}
                        >
                          <option value="">All</option>
                          {STOCK_CATEGORY_FILTER_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1 d-block"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          View
                        </label>
                        <div className="btn-group" role="group">
                          <button
                            type="button"
                            className={`btn btn-sm ${
                              viewMode === "grid"
                                ? "btn-info"
                                : "btn-outline-secondary"
                            }`}
                            onClick={() => setViewMode("grid")}
                            title="Grid view"
                            style={{
                              backgroundColor:
                                viewMode === "grid" ? "#1199B2" : undefined,
                              borderColor:
                                viewMode === "grid" ? "#1199B2" : undefined,
                              color: viewMode === "grid" ? "#fff" : undefined,
                            }}
                          >
                            <i className="fa-solid fa-th mr-1" />
                            Grid
                          </button>
                          <button
                            type="button"
                            className={`btn btn-sm ${
                              viewMode === "list"
                                ? "btn-info"
                                : "btn-outline-secondary"
                            }`}
                            onClick={() => setViewMode("list")}
                            title="List view"
                            style={{
                              backgroundColor:
                                viewMode === "list" ? "#1199B2" : undefined,
                              borderColor:
                                viewMode === "list" ? "#1199B2" : undefined,
                              color: viewMode === "list" ? "#fff" : undefined,
                            }}
                          >
                            <i className="fa-solid fa-list mr-1" />
                            List
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {isLoading ? (
                    <div className="text-center p-4">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  ) : inventories.length === 0 && !hasActiveFilters ? (
                    <div className="nocontent_div text-center p-4">
                      There are no inventory records yet.
                    </div>
                  ) : inventories.length === 0 && hasActiveFilters ? (
                    <div className="nocontent_div text-center p-4">
                      No inventory found matching your filters.
                    </div>
                  ) : viewMode === "grid" ? (
                    renderGridView()
                  ) : (
                    renderListView()
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* View Inventory Modal */}
      <div
        className="modal fade"
        id="modalviewinventory"
        tabIndex={-1}
        aria-labelledby="modalviewinventoryLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div
            className="modal-content"
            style={{ border: "none", borderRadius: "10px", overflow: "hidden" }}
          >
            <div
              className="modal-header border-0"
              style={{
                backgroundColor: "#1199B2",
                color: "#fff",
                padding: "16px 20px",
              }}
            >
              <h5 className="modal-title mb-0" id="modalviewinventoryLabel">
                <i className="fa-solid fa-box-open mr-2" />
                View Inventory
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                onClick={handleCloseViewModal}
                style={{ color: "#fff", opacity: 1, textShadow: "none" }}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div
              className="modal-body"
              style={{ padding: "20px", backgroundColor: "#fff" }}
            >
              {isLoadingViewItem ? (
                <div className="text-center py-5">
                  <PulseLoader color="#1199B2" size={10} />
                  <p className="text-muted small mt-3 mb-0">
                    Loading inventory details...
                  </p>
                </div>
              ) : viewItem ? (
                <>
                  <div
                    className="mb-4 p-3"
                    style={{
                      background:
                        "linear-gradient(135deg, #f0f9fb 0%, #e8f4f8 100%)",
                      border: "1px solid #cfe8ef",
                      borderRadius: "10px",
                    }}
                  >
                    <div
                      className="d-flex align-items-start justify-content-between"
                      style={{ gap: "16px" }}
                    >
                      <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#1199B2",
                            textTransform: "uppercase",
                            letterSpacing: "0.4px",
                            marginBottom: "6px",
                          }}
                        >
                          Product Name
                        </div>
                        <div
                          style={{
                            fontSize: "18px",
                            fontWeight: 700,
                            color: "#17324d",
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {displayValue(viewItem.productName)}
                        </div>
                      </div>
                      <div
                        className="d-flex flex-column align-items-end flex-shrink-0"
                      >
                        <div
                          className="badge badge-light text-dark"
                          style={{
                            fontSize: "12px",
                            padding: "6px 10px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {displayValue(viewItem.materialCode)}
                        </div>
                        <div className="mt-2">
                          {renderStatusBadge(viewItem.status)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-sm-6 mb-3">
                      <InventoryDetailCard
                        label="HSN Code"
                        value={displayValue(viewItem.hsnCode)}
                        icon="fa-solid fa-hashtag"
                      />
                    </div>
                    <div className="col-sm-6 mb-3">
                      <InventoryDetailCard
                        label="UOM"
                        value={displayValue(viewItem.uom)}
                        icon="fa-solid fa-ruler"
                      />
                    </div>
                    <div className="col-sm-6 mb-3">
                      <InventoryDetailCard
                        label="Stock Category"
                        value={displayValue(viewItem.stockCategory)}
                        icon="fa-solid fa-tags"
                      />
                    </div>
                    <div className="col-sm-6 mb-3">
                      <InventoryDetailCard
                        label="Stock Quantity"
                        value={
                          viewItem.stockQuantity != null
                            ? String(viewItem.stockQuantity)
                            : "-"
                        }
                        icon="fa-solid fa-cubes"
                      />
                    </div>
                  </div>

                  <div className="row mt-2">
                    <div className="col-md-6 mb-3 mb-md-0">
                      <div
                        style={{
                          backgroundColor: "#f8fafb",
                          border: "1px solid #e3e8ee",
                          borderRadius: "8px",
                          padding: "14px 16px",
                          height: "100%",
                        }}
                      >
                        <div
                          className="d-flex align-items-center mb-3"
                          style={{
                            color: "#6c757d",
                            fontSize: "12px",
                            fontWeight: 600,
                          }}
                        >
                          <i
                            className="fa-solid fa-images mr-2"
                            style={{ color: "#1199B2" }}
                          />
                          Images
                        </div>
                        <InventoryViewImages
                          images={getInventoryImages(viewItem)}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div
                        style={{
                          backgroundColor: "#f8fafb",
                          border: "1px solid #e3e8ee",
                          borderRadius: "8px",
                          padding: "14px 16px",
                          height: "100%",
                        }}
                      >
                        <div
                          className="d-flex align-items-center mb-3"
                          style={{
                            color: "#6c757d",
                            fontSize: "12px",
                            fontWeight: 600,
                          }}
                        >
                          <i
                            className="fa-solid fa-paperclip mr-2"
                            style={{ color: "#1199B2" }}
                          />
                          Attachments
                        </div>
                        <InventoryViewAttachments
                          attachments={getInventoryAttachments(viewItem)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row mt-3">
                    <div className="col-sm-6 mb-3">
                      <InventoryDetailCard
                        label="Created By"
                        value={displayUser(viewItem.createdBy)}
                        icon="fa-solid fa-user"
                      />
                    </div>
                    <div className="col-sm-6 mb-3">
                      <InventoryDetailCard
                        label="Updated By"
                        value={displayUser(viewItem.updatedBy)}
                        icon="fa-solid fa-user-pen"
                      />
                    </div>
                    <div className="col-sm-6 mb-3">
                      <InventoryDetailCard
                        label="Created At"
                        value={formatDateToDisplay(viewItem.createdAt)}
                        icon="fa-regular fa-calendar"
                      />
                    </div>
                    <div className="col-sm-6 mb-3">
                      <InventoryDetailCard
                        label="Updated At"
                        value={formatDateToDisplay(viewItem.updatedAt)}
                        icon="fa-regular fa-clock"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center text-muted py-5">
                  <i className="fa-regular fa-folder-open fa-2x mb-3 d-block" />
                  Inventory details not available
                </div>
              )}
            </div>
            <div
              className="modal-footer border-0 justify-content-center"
              style={{ backgroundColor: "#f8fafb", padding: "14px 20px" }}
            >
              <button
                type="button"
                className="btn btn-secondary px-4"
                data-dismiss="modal"
                onClick={handleCloseViewModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit / Update Stock Modal */}
      <div
        className="modal fade"
        id="modaleditinventory"
        tabIndex={-1}
        aria-labelledby="modaleditinventoryLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modaleditinventoryLabel">
                Update Inventory Stock
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                onClick={handleCloseEditModal}
                disabled={isUpdatingStock}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              {editItem ? (
                <>
                  <div className="mb-3 p-3" style={{ background: "#f8fafb", borderRadius: "8px" }}>
                    <div className="fwt_600">
                      {displayValue(editItem.materialCode)}
                    </div>
                    <div className="text-muted" style={{ fontSize: "13px" }}>
                      {displayValue(editItem.productName)}
                    </div>
                    <div className="mt-2" style={{ fontSize: "13px" }}>
                      Current Stock:{" "}
                      <span className="fwt_600">
                        {editItem.stockQuantity != null
                          ? editItem.stockQuantity
                          : "-"}
                      </span>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>
                      Operation <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-control"
                      value={editOperation}
                      onChange={(e) => setEditOperation(e.target.value)}
                      disabled={isUpdatingStock}
                    >
                      {STOCK_OPERATIONS.map((op) => (
                        <option key={op.value} value={op.value}>
                          {op.label}
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">
                      Add increases stock, Subtract decreases.
                    </small>
                  </div>

                  <div className="form-group mb-0">
                    <label>
                      Quantity <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      min="0"
                      step="1"
                      placeholder="Enter quantity"
                      value={editCount}
                      onChange={(e) => setEditCount(e.target.value)}
                      disabled={isUpdatingStock}
                    />
                  </div>
                </>
              ) : (
                <div className="text-center text-muted py-4">
                  Select an inventory record to update
                </div>
              )}
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
                onClick={handleCloseEditModal}
                disabled={isUpdatingStock}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-blue"
                onClick={handleUpdateStock}
                disabled={isUpdatingStock || !editItem}
              >
                {isUpdatingStock ? "Updating..." : "Update Stock"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* History Modal */}
      <div
        className="modal fade"
        id="modalinventoryhistory"
        tabIndex={-1}
        aria-labelledby="modalinventoryhistoryLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modalinventoryhistoryLabel">
                Inventory History
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                onClick={handleCloseHistoryModal}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              {isLoadingHistoryItem ? (
                <div className="text-center py-4">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : historyItem ? (
                <>
                  <div className="mb-3">
                    <div className="fwt_600">
                      {displayValue(historyItem.materialCode)} —{" "}
                      {displayValue(historyItem.productName)}
                    </div>
                    <div className="text-muted" style={{ fontSize: "13px" }}>
                      Current Stock:{" "}
                      {historyItem.stockQuantity != null
                        ? historyItem.stockQuantity
                        : "-"}
                    </div>
                  </div>

                  {(() => {
                    const historyRows = getInventoryHistory(historyItem);
                    if (historyRows.length === 0) {
                      return (
                        <div className="text-center text-muted py-4">
                          No history records found
                        </div>
                      );
                    }

                    return (
                      <div className="table-responsive">
                        <table className="table table-bordered table-striped mb-0 txt_lft">
                          <thead>
                            <tr>
                              <th style={{ width: "50px" }}>S.No</th>
                              <th>Count</th>
                              <th>Expression</th>
                              <th>Operation</th>
                              <th>Module</th>
                              <th>Updated By</th>
                              <th>Updated At</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...historyRows].reverse().map((row, idx) => (
                              <tr key={`${row.updatedAt || idx}-${idx}`}>
                                <td>{idx + 1}</td>
                                <td className="fwt_600">
                                  {displayValue(row.Count ?? row.count)}
                                </td>
                                <td
                                  className="fwt_600"
                                  style={{ whiteSpace: "nowrap" }}
                                >
                                  {displayValue(
                                    row.expression || row.Expression,
                                  )}
                                </td>
                                <td style={{ textTransform: "capitalize" }}>
                                  {displayValue(
                                    row.operation || row.Operation,
                                  )}
                                </td>
                                <td>
                                  {displayValue(row.Module || row.module)}
                                </td>
                                <td>
                                  {resolveHistoryUpdatedBy(
                                    row.updatedBy,
                                    historyItem,
                                  )}
                                </td>
                                <td>
                                  {formatDateToDisplay(row.updatedAt)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <div className="text-center text-muted py-4">
                  History not available
                </div>
              )}
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary"
                data-dismiss="modal"
                onClick={handleCloseHistoryModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Inventory;
